import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { EnglishDateTimeInput } from '@/components/ui/EnglishDateInput';
import { InlineFieldError } from '@/components/ui/FormFeedback';
import type { Order } from '@/types/order';
import { parseServiceTimeToDateAndSlot, timeSlotToServiceTime } from '@/api/modules/order';
import { listOrderAssignedStaff } from '@/lib/settleAllocation';
import { formatServiceTime } from '@/utils/formatServiceTime';

export interface ReorderOrderConfirmPayload {
  date: string;
  /** 24 小时制 HH:MM */
  time: string;
  otherInfo?: string;
}

interface ReorderOrderModalProps {
  order: Order | null;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (payload: ReorderOrderConfirmPayload) => void;
}

function resolveOriginalTimeLabel(serviceTime: string): string {
  const formatted = formatServiceTime(serviceTime);
  if (formatted === '—') return 'Original order time';
  return formatted;
}

const DEFAULT_TIME_SLOT = '09:00 AM';

function slotFromServiceTime(serviceTime: string): string {
  const slot = parseServiceTimeToDateAndSlot(serviceTime)?.timeSlot;
  return slot && /^\d{1,2}:\d{2}\s*(AM|PM)$/i.test(slot) ? slot : DEFAULT_TIME_SLOT;
}

export default function ReorderOrderModal({
  order,
  submitting,
  onClose,
  onConfirm,
}: ReorderOrderModalProps) {
  const [serviceDate, setServiceDate] = useState('');
  const [timeSlot, setTimeSlot] = useState(DEFAULT_TIME_SLOT);
  const [otherInfo, setOtherInfo] = useState('');
  const [appointmentError, setAppointmentError] = useState<string | undefined>();

  const minDate = useMemo(() => dayjs().format('YYYY-MM-DD'), []);
  const defaultStaff = useMemo(() => (order ? listOrderAssignedStaff(order) : []), [order]);

  useEffect(() => {
    if (!order) {
      setServiceDate('');
      setTimeSlot(DEFAULT_TIME_SLOT);
      setOtherInfo('');
      setAppointmentError(undefined);
      return;
    }
    setServiceDate('');
    setTimeSlot(slotFromServiceTime(order.service_time));
    setOtherInfo(order.notes ?? '');
    setAppointmentError(undefined);
  }, [order]);

  if (!order) return null;

  const handleConfirm = () => {
    if (!serviceDate.trim() || !timeSlot.trim()) {
      setAppointmentError('Please select a date and time.');
      return;
    }
    const serviceTime = timeSlotToServiceTime(serviceDate.trim(), timeSlot);
    if (dayjs(serviceTime).isBefore(dayjs())) {
      setAppointmentError('Choose a later service time.');
      return;
    }
    setAppointmentError(undefined);
    const clock = serviceTime.slice(11, 19);
    onConfirm({
      date: serviceDate.trim(),
      time: clock,
      otherInfo: otherInfo.trim() || undefined,
    });
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <button
          type="button"
          className="absolute inset-0 bg-black/40"
          aria-label="Close"
          onClick={onClose}
        />
      <div className="relative z-10 w-full max-w-md bg-white rounded-lg border border-gray-200 max-h-[88vh] flex flex-col">
        <div className="p-3 pb-3 flex-shrink-0">
          <div className="text-gray-800 font-bold text-base mb-1">Reorder Service</div>
          <div className="text-gray-400 text-xs font-mono">{order.id}</div>
        </div>

        <div className="px-4 pb-5 overflow-y-auto flex-1 min-h-0 space-y-3">
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 space-y-2">
            <div className="flex items-start justify-between gap-3 text-xs">
              <span className="text-gray-500 font-medium flex-shrink-0">Customer</span>
              <span className="text-gray-800 font-semibold text-right">{order.customer.name}</span>
            </div>
            <div className="flex items-start justify-between gap-3 text-xs">
              <span className="text-gray-500 font-medium flex-shrink-0">Service</span>
              <span className="text-gray-800 font-semibold text-right">{order.serviceType}</span>
            </div>
            <div className="flex items-start justify-between gap-3 text-xs">
              <span className="text-gray-500 font-medium flex-shrink-0">Address</span>
              <span className="text-gray-700 text-right leading-relaxed">{order.customer.address}</span>
            </div>
            <div className="flex items-start justify-between gap-3 text-xs">
              <span className="text-gray-500 font-medium flex-shrink-0">Original Time</span>
              <span className="text-gray-700 text-right">{resolveOriginalTimeLabel(order.service_time)}</span>
            </div>
            {typeof order.totalPrice === 'number' && order.totalPrice > 0 && (
              <div className="flex items-center justify-between gap-3 text-xs pt-1 border-t border-gray-200/80">
                <span className="text-gray-500 font-medium">Total</span>
                <span className="text-green-600 font-semibold">A${order.totalPrice.toFixed(2)}</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-normal text-gray-600 mb-2">
              Appointment <span className="text-red-500">*</span>
            </label>
            <EnglishDateTimeInput
              id="reorder-appointment"
              date={serviceDate}
              time={timeSlot}
              min={minDate}
              pickerTitle="Appointment"
              placeholder="Select date and time"
              onChange={(nextDate, nextTime) => {
                setServiceDate(nextDate);
                setTimeSlot(nextTime);
                setAppointmentError(undefined);
              }}
              aria-invalid={!!appointmentError}
              aria-describedby={appointmentError ? 'reorder-appointment-error' : undefined}
              className={
                appointmentError
                  ? 'border-red-300 bg-red-50/35 focus:border-red-400'
                  : 'border-gray-200 focus:border-green-500'
              }
            />
            <InlineFieldError id="reorder-appointment-error" message={appointmentError} />
            <p className="text-[10px] text-gray-500 leading-relaxed mt-2">
              {defaultStaff.length > 0
                ? `Default staff: ${defaultStaff.map((person) => person.name).join(', ')}. You can reassign them after the order is created.`
                : 'No previous staff on this order. You can assign staff after it is created.'}
            </p>
          </div>

          <div>
            <label className="block text-xs font-normal text-gray-600 mb-2">Notes (optional)</label>
            <textarea
              value={otherInfo}
              onChange={(e) => setOtherInfo(e.target.value)}
              disabled={submitting}
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-green-500 disabled:opacity-60 resize-none"
              placeholder="Leave a message for the platform…"
            />
          </div>
        </div>

        <div className="p-3 pt-3 border-t border-gray-100 flex-shrink-0 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 h-11 rounded-lg border border-gray-200 text-gray-600 text-sm font-normal cursor-pointer disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="flex-1 h-11 rounded-lg bg-violet-600 text-white text-sm font-normal cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Reordering…
              </>
            ) : (
              <>
                <i className="ri-repeat-line" />
                Confirm Reorder
              </>
            )}
          </button>
        </div>
      </div>
      </div>
    </>
  );
}
